import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

/**
 * BabyOS Button — pill CTA with ref1+ref2 style variants.
 * - primary  = coral pill (ref1 warm)   — main actions, "Log feed", "Save"
 * - success  = mint pill  (ref2 fresh)  — confirmations, "Mark resolved"
 * - ghost    = frosted glass            — secondary, "Cancel"
 * - danger   = rose                     — destructive
 * - icon     = 44×44 circle for header
 */
type Variant = 'primary' | 'success' | 'ghost' | 'danger' | 'icon' | 'secondary' | 'destructive'

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'sm' | 'md' | 'lg'
  /** @deprecated legacy pack tint — no-op in v9999 design */
  packColor?: string
  loading?: boolean
  children: ReactNode
}

function normalizeVariant(v: Variant): 'primary' | 'success' | 'ghost' | 'danger' | 'icon' {
  if (v === 'secondary')   return 'ghost'
  if (v === 'destructive') return 'danger'
  return v
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, children, className = '', style, packColor: _packColor, ...rest },
  ref,
) {
  variant = normalizeVariant(variant) as Variant
  if (variant === 'icon') {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`icon-btn ${className}`}
        style={style}
        {...rest}
      >
        {loading ? <Spinner /> : children}
      </button>
    )
  }

  const sizeCls =
    size === 'sm'
      ? 'text-caption px-4 py-2 min-h-[40px]'
      : size === 'lg'
        ? 'text-lg px-8 py-4 min-h-[60px]'
        : 'text-body px-6 py-3 min-h-[52px]'

  let variantCls = ''
  if (variant === 'primary')  variantCls = 'pill-btn pill-btn--coral'
  if (variant === 'success')  variantCls = 'pill-btn pill-btn--mint'
  if (variant === 'ghost')    variantCls = 'pill-btn pill-btn--ghost'
  if (variant === 'danger')   variantCls = 'pill-btn'

  const dangerStyle: React.CSSProperties = variant === 'danger'
    ? {
        background: 'linear-gradient(180deg, #F0728A 0%, #E85E6E 55%, #D14758 100%)',
        color: 'white',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 10px 28px -8px rgba(232,94,110,0.55)',
      }
    : {}

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${variantCls} ${sizeCls} ${className}`}
      style={{ ...dangerStyle, ...style }}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  )
})

function Spinner() {
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" className="animate-spin">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" fill="none" />
      <path d="M14 8a6 6 0 00-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  )
}
